<template>
    <div class="load-mask-parent query-ai-component">
        <component-title
            :config="config">
        </component-title>
        <div :class="loadMask"></div>
        <div class="query-ai-content">
            <template v-for="(message) in messages">
                <p :class="'query-ai-content-dialog query-ai-content-dialog-' + message.getType()" v-html="message.getMessage()"></p>
            </template>
        </div>
        <div class="textarea-placeholder">
            <textarea
                maxlength="1000"
                @keyup.enter="pushMessage"
                v-model="value"
                :placeholder="t('Type your message here') + '...'"
            >
            </textarea>
            <button class="btn btn-confirm scroll-top-btn" @click="scrollToTop">{{ t('Scroll to top') }}</button>
            <button class="btn btn-info push-message-btn" @click="pushMessage">{{ t('Send') }}</button>
        </div>
    </div>
</template>

<script>
import ComponentTitle from "@/components/ComponentTitle.vue";
import ValueChecker from "@/services/ValueChecker";
import Message from "@/Models/Message.ts";

export default {
    components: {ComponentTitle},
    props: {
        config: {
            Type: Object,
            default: function () {
                return {
                    index: null,
                    loadUrl: null,
                    emitEvents: {},
                    components: [],
                    description: [],
                    createMessageUrl: null,
                    createSessionUrl: null,
                }
            }
        },
        eventBus: {
            Type: Object,
        },
        routeParams: {
            Type: Object
        },
        queryParams: {
            Type: Object
        },
    },
    data() {
        return {
            loadMask: '',
            value: null,
            messages: [],
            messagesStamp: [],
            token: null,
        };
    },
    beforeDestroy() {
        this.eventSourceService.removeQueue('query-ai-event-source');
        this.unmountEvents();
    },
    inject: ['eventManager', 'eventSourceService'],
    async mounted() {
        this.init();
        this.mountEvents();

        if (this.queryParams.token) {
            await this.load(this.queryParams.token);
        } else {
            await this.initToken();
        }

        this.eventSourceService.addQueue('query-ai-event-source', {token: this.token});
        this.eventManager.emitEvents(this.config.emitEvents['mounted'] || [], []);
    },
    methods: {
        init() {
            this.componentIndex = this.config.index;
            this.components = this.config.components;
            this.value = this.config.value;
        },
        async initToken() {
            if (!this.token) {
                this.addLoadMask();
                let response = await this.createSession();
                this.token = response.data.token;
                this.removeLoadMask();
            }
        },
        onMessage(data) {
            (data || []).forEach(item => {
                let message = Message.createFromJson(item);
                this.pushMessageOnStack(message);
            });
        },
        mountEvents() {
            this.eventManager.bindEvents(this.defaultBindEvents(), this);
            this.eventBus.$on('EventSourceService:query-ai-event-source:onMessage', this.onMessage);
        },
        unmountEvents() {
            this.eventManager.unbindEvents(this.defaultBindEvents());
            this.eventBus.$off('EventSourceService:query-ai-event-source:onMessage');
        },
        async load(token) {
            this.addLoadMask();
            let response = await this.fetchSession({token: token});
            this.token = token;

            response.data.forEach(item => {
                let message = Message.createFromJson(item);
                this.pushMessageOnStack(message);
            });

            this.removeLoadMask();
        },
        async fetchSession(params) {
            const url = this.config['loadUrl'];

            return await this.$http['get'](url, {params: params});
        },
        async createMessage(message, type_id, token) {
            const url = this.config['createMessageUrl'];
            let payload = {message: message, typeId: type_id, token: token};
            return await this.$http['post'](url, payload);
        },
        async createSession() {
            const url = this.config['createSessionUrl'];

            return await this.$http['post'](url, {});
        },
        addLoadMask() {
            this.loadMask = 'lmask';
        },
        removeLoadMask() {
            this.loadMask = '';
        },
        defaultBindEvents() {
            return [];
        },
        async pushMessage(event) {
            let value = (this.value || '').trim();

            if (ValueChecker.isUndefinedOrNullOrIsEmpty(value)) {
                return;
            }

            this.addLoadMask();
            this.value = null;
            let response = await this.createMessage(value, 1, this.token);
            let message = Message.createFromJson(response.data);
            this.pushMessageOnStack(message);
            this.removeLoadMask();
            let el = this.$el.querySelector('.query-ai-content p:last-child');
            this.scrollToElement(el);
        },
        pushMessageOnStack(message) {
            let messageStamp = this.createMessageStamp(message);

            if (this.messagesStamp.includes(messageStamp)) {
                return;
            }

            this.messagesStamp.push(messageStamp);
            this.messages.push(message);
        },
        createMessageStamp(message) {
            return message.getId();
        },
        scrollToElement(el) {
            if (!el) {
                return;
            }

            el.scrollIntoView({behavior: 'smooth'});
        },
        scrollToTop() {
            let el = this.$el.querySelector('.component-title-box');
            this.scrollToElement(el);
        }
    },
    computed: {

    },
}

</script>

<style lang="sass" scoped>
.query-ai-component {
    position: relative;
    padding: 15px 300px 15px 300px!important;
    color: #333;
}
.query-ai-content {
    overflow: auto;
    padding: 15px;
    margin-bottom: 10vh;
}
.query-ai-content-dialog {
    border-radius: 15px;
    padding: 10px;
    font-size: 1.2em;
    width: fit-content;
}
.query-ai-content-dialog-1 {
    border: 1px solid #dedede;
    text-align: center;
    margin: 0 100px 15px 0;
}
.query-ai-content-dialog-2 {
    background-color: #eaf5eb;
    border: 1px solid #4baf4d;
    text-align: center;
    margin: 0 0 15px 100px;
    list-style: disc inside none;
}
.textarea-placeholder {
    position: sticky;
    box-sizing: border-box;
    bottom: 15px;
    left: 300px;
    right: 300px;
    padding-left: 15px;
    padding-right: 15px;
}
textarea {
    min-height: 150px;
    padding: 1em;
    font-size: 1.2em;
    text-align: left;
    resize: none;
    width: 100%;
    border: 2px solid #919191;
    border-radius: 10px;
}
.scroll-top-btn {
    border-radius: 10px;
    position: absolute;
    bottom: 120px;
    margin-left: 3px;
}
.push-message-btn {
    border-radius: 10px;
    position: absolute;
    bottom: 80px;
    margin-left: 3px;
    background-color: #4698ca;
}
</style>


// WEBPACK FOOTER //
// QueryAIComponent.vue?203eef38